<script>
  import { Icon, CheckCircle } from "svelte-hero-icons";
  import { onMount } from "svelte";
  import { NotificationBellIcon } from "$lib/Elements/Icons";
  import { createNotificationsStore } from "./Knock/notifications";
  import { clickOutside } from "$utils/clickOutside";
  import Notification from "./Knock/Notification.svelte";

  // svelte-ignore unused-export-let
  export let live;
  export let public_key;
  export let signed_key;
  export let channel_id;
  export let user_id;
  export let tenant_id;
  export let variant = "compact";

  let isOpen = false;
  let notifications = createNotificationsStore();
  let viewButton;

  $: isSidebar = variant === "sidebar";

  // In the sidebar the button lives in the far-left rail, so the panel flies
  // out to the right (mirroring the sidebar tooltips' `placement="right"`),
  // anchored to the top of the button so it grows downward into open space.
  // In the compact header the button sits near the top-right, so the panel
  // hangs down and aligns to the right edge.
  $: positionClass = isSidebar ? "left-full top-0 ml-2" : "top-full right-0 mt-2";

  function toggleOpen() {
    isOpen = !isOpen;
  }

  function close() {
    isOpen = false;
  }

  onMount(async () => {
    // Remove the server-rendered placeholder bell(s) once the real component mounts.
    // Query all matches (not getElementById, which only finds the first) so a
    // leftover placeholder never lingers if more than one is ever present in the DOM.
    document.querySelectorAll("[data-notif-placeholder]").forEach((el) => el.remove());

    // Initialize the notifications store. The feed is scoped to the active org
    // (tenant) so the bell only shows notifications for the org the user is in.
    await notifications.init({ public_key, signed_key, user_id, channel_id, tenant: tenant_id });
  });
</script>

<div class={isSidebar ? "relative w-full" : "relative"}>
  {#if isSidebar}
    <button
      bind:this={viewButton}
      on:click={toggleOpen}
      type="button"
      class="group flex w-full items-center gap-x-3 rounded-md p-2 text-sm font-medium text-gray-900 hover:bg-gray-200/60 dark:text-gray-200 dark:hover:bg-[#2a2c32] sidebar-collapsed:justify-center relative cursor-pointer"
    >
      <span class="relative shrink-0">
        <Icon src={NotificationBellIcon} class="h-5 w-5 shrink-0" />
        {#if $notifications.unreadCount > 0}
          <span
            class="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] inline-flex items-center justify-center"
          >
            {$notifications.unreadCount}
          </span>
        {/if}
      </span>
      <span class="truncate sidebar-collapsed:hidden">Notifications</span>
    </button>
  {:else}
    <button
      bind:this={viewButton}
      on:click={toggleOpen}
      type="button"
      class="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-500 relative cursor-pointer"
    >
      <span class="sr-only">View notifications</span>
      <Icon src={NotificationBellIcon} class="h-6 w-6" />
      {#if $notifications.unreadCount > 0}
        <span
          class="absolute w-4 h-4 top-1.5 right-1.5 bg-red-500 rounded-full text-white text-[9px] inline-flex items-center justify-center"
        >
          {$notifications.unreadCount}
        </span>
      {/if}
    </button>
  {/if}

  <div
    use:clickOutside={[viewButton]}
    on:clickoutside={close}
    class:hidden={!isOpen}
    class="absolute {positionClass} z-50 w-[80vw] max-w-[calc(100vw-2rem)] md:w-[400px]"
  >
    <div
      class="bg-white text-gray-900 rounded border border-gray-200 shadow-md pt-4 text-sm divide-y divide-gray-200 dark:bg-[#1f2127] dark:text-gray-200 dark:border-white/10 dark:divide-white/10 dark:shadow-black/40"
    >
      <div class="inline-flex justify-between w-full px-4 pb-4 whitespace-nowrap">
        <span class="font-bold"
          >Notifications <span class="font-normal text-xs align-text-bottom"
            >({$notifications.totalCount})</span
          ></span
        >
        <button
          class:hidden={$notifications.isWaiting}
          class="inline-flex items-center hover:text-slate-600 text-slate-400 cursor-pointer"
          on:click={() => notifications.markAllRead()}
        >
          Mark all as read <Icon src={CheckCircle} class="ml-1 w-4 h-4" />
        </button>
      </div>

      <div
        class:hidden={!$notifications.isWaiting}
        class="pt-6 pb-4 flex flex-col gap-4 justify-center items-center"
      >
        <div class="text-slate-400">Waiting for notifications...</div>
      </div>

      <div
        class:hidden={$notifications.notifications.length > 0}
        class="pt-6 pb-4 flex flex-col gap-4 justify-center items-center"
      >
        <div class="text-slate-400">No notifications</div>
      </div>

      <div
        class:hidden={$notifications.isWaiting || $notifications.notifications.length === 0}
        class="divide-y divide-gray-200 dark:divide-white/10 max-h-96 overflow-y-auto"
      >
        {#each $notifications.notifications as item}
          <Notification
            {item}
            on:read={({ detail }) => notifications.markRead(detail.id)}
            on:archive={({ detail }) => notifications.archive(detail.id)}
          />
        {/each}
      </div>
    </div>
  </div>
</div>
